import { ChertoExperiments } from 'constants/experiments'
import useUserReviewsSummary from 'hooks/Reviews/useUserReviewsSummary'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useIsDesktopWithSsrFallback } from 'hooks/useIsDesktopWithSsrFallback'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { useEffect } from 'react'

export const FLASH_DESKTOP_HEADER_MIN_REVIEW_COUNT = 20
export const FLASH_DESKTOP_HEADER_MIN_REVIEW_SCORE = 7
export const FLASH_DESKTOP_HEADER_SUMMARY_CHARACTER_LIMIT = 210
const EXCLUDE_OFFER_HEADER_EXPERIMENT_CAMPAIGN_TAG = 'Exclude Offer Header Experiment'
const HEADER_REVIEW_SUMMARY_OPTIONS = { maxCharacters: FLASH_DESKTOP_HEADER_SUMMARY_CHARACTER_LIMIT }
const DISABLED_HEADER_REVIEW_SUMMARY_OPTIONS = { enabled: false }
const EXPERIMENT_OPTIONS = { canActivate: false }

function hasFlashDesktopHeaderReviewScore(score: number | undefined): boolean {
  return (score ?? 0) > FLASH_DESKTOP_HEADER_MIN_REVIEW_SCORE
}

interface FlashDesktopOfferHeaderReviewSummaryGate {
  reviewsTotal: number | undefined
  reviewScore: number | undefined
  authoredSummary: string | undefined
  fetchedSummary: string | undefined
  isFetchedSummaryLoading: boolean
  hasFetchedSummaryError: boolean
}

export function canEnterFlashDesktopOfferHeaderExperiment(
  params: FlashDesktopOfferHeaderReviewSummaryGate,
): boolean {
  if ((params.reviewsTotal ?? 0) < FLASH_DESKTOP_HEADER_MIN_REVIEW_COUNT) {
    return false
  }

  if (!hasFlashDesktopHeaderReviewScore(params.reviewScore)) {
    return false
  }

  const authoredSummary = params.authoredSummary?.trim()
  if (authoredSummary) {
    return true
  }

  if (params.isFetchedSummaryLoading || params.hasFetchedSummaryError) {
    return true
  }

  return !!params.fetchedSummary?.trim()
}

function useFlashDesktopOfferHeaderExperiment(
  offer: App.AnyOffer | undefined,
) {
  const isDesktop = useIsDesktopWithSsrFallback()
  const isAudience = isFlashHotelOffer(offer) &&
    offer.property.rating?.reviewsSource !== 'google' &&
    !offer.campaignTags.includes(EXCLUDE_OFFER_HEADER_EXPERIMENT_CAMPAIGN_TAG)
  const property = isFlashHotelOffer(offer) ? offer.property : undefined
  const authoredSummary = property?.aiReviewSummaryOverride?.trim() || undefined
  const reviewScore = property?.rating?.score
  const hasEnoughReviews = (property?.rating?.reviewsTotal ?? 0) >= FLASH_DESKTOP_HEADER_MIN_REVIEW_COUNT
  const hasHighEnoughScore = hasFlashDesktopHeaderReviewScore(reviewScore)
  const experiment = useExperiment(ChertoExperiments.croFlashDesktopOfferHeader, EXPERIMENT_OPTIONS)
  const { isTurnedOn, triggerActivation } = experiment
  const shouldFetchSummary = isDesktop && isAudience && isTurnedOn && hasEnoughReviews && hasHighEnoughScore && !authoredSummary
  const [reviewSummary, fetchingReviewSummary, reviewSummaryError] = useUserReviewsSummary(
    property?.id,
    'luxuryescapes',
    shouldFetchSummary ? HEADER_REVIEW_SUMMARY_OPTIONS : DISABLED_HEADER_REVIEW_SUMMARY_OPTIONS,
  )
  const canEnter = canEnterFlashDesktopOfferHeaderExperiment({
    reviewsTotal: property?.rating?.reviewsTotal,
    reviewScore,
    authoredSummary,
    fetchedSummary: reviewSummary?.summary,
    isFetchedSummaryLoading: shouldFetchSummary && fetchingReviewSummary,
    hasFetchedSummaryError: shouldFetchSummary && !!reviewSummaryError,
  })
  const isEligible = isAudience && canEnter
  useEffect(() => {
    if (isDesktop && isEligible && isTurnedOn) {
      triggerActivation()
    }
  }, [isDesktop, isEligible, isTurnedOn, triggerActivation])

  return {
    ...experiment,
    isVariant: experiment.isAnyVariant && isEligible,
  }
}

export default useFlashDesktopOfferHeaderExperiment
